import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { toApiError, useApi, type ApiError } from '@/shared/api';
import { Field, FieldGrid } from './PanelCard';

// DEV-Vorschau-Werte (kein Fetch im Demo).
const DEMO_ACCESS = { doorCode: '4711', wiFiPassword: 'loft-guest-2026' };

// Fehlercodes, die der Messaging-Service selbst vergibt: eine Antwort, die sich durch Wiederholen
// nicht aendert. Alles andere (5xx, 429, abgelaufenes Token, Netz) bleibt wiederholbar.
const TERMINAL_CODES = [
  'apartment_selection_required',
  'apartment_not_in_conversation',
  'access_info_unavailable',
  'conversation_not_found',
];

// Gated Zugangsdaten: erst auf Klick geladen (eigener Endpoint, Admin/Staff, auditiert) — nie im
// Panel-Batch. bookingId benennt das Apartment, wenn der Thread an einer Reservierung ueber mehrere
// haengt; ohne Auswahl lehnt der Server dort ab, statt leere Felder zu liefern.
export function AccessInfoReveal({
  threadId,
  bookingId,
  demo,
}: {
  threadId: string;
  bookingId?: string | null;
  demo?: boolean;
}) {
  const api = useApi();
  const [revealed, setRevealed] = useState(false);
  const load = useMutation<Awaited<ReturnType<typeof api.messaging.accessInfo>>, ApiError>({
    mutationFn: async () => {
      try {
        return await api.messaging.accessInfo(threadId, bookingId);
      } catch (err) {
        throw await toApiError(err);
      }
    },
  });

  if (!revealed) {
    return (
      <button
        onClick={() => {
          setRevealed(true);
          if (!demo) load.mutate();
        }}
        className="text-postmark mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium"
      >
        <KeyRound size={13} /> Türcode &amp; WLAN-Passwort anzeigen
      </button>
    );
  }

  if (!demo && load.isPending) {
    return <p className="text-ink-faint mt-1 text-[12px]">Lade …</p>;
  }

  // Eine fachliche Ablehnung ist terminal — sie als wiederholbar zu rendern schickt den Nutzer in
  // eine Schleife ohne Ausgang. Terminal ist aber eine Eigenschaft der ANTWORT, nicht jeder
  // Statusnummer: 500/502/429 und ein abgelaufenes Token sind wiederholbar. Der Server nennt seinen
  // Fehlercode im ProblemDetails-Title, daran haengt die Unterscheidung.
  if (!demo && load.isError) {
    return TERMINAL_CODES.includes(load.error.title) ? (
      <p className="text-sealwax mt-1 text-[12px]">{load.error.detail ?? load.error.title}</p>
    ) : (
      <button
        onClick={() => load.mutate()}
        className="text-sealwax mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium"
      >
        <KeyRound size={13} /> Nicht geladen — erneut versuchen
      </button>
    );
  }

  const data = demo ? DEMO_ACCESS : load.data;
  return (
    <div className="mt-2">
      <FieldGrid>
        <Field label="Türcode" mono value={data?.doorCode} />
        <Field label="WLAN-Passwort" mono value={data?.wiFiPassword} />
      </FieldGrid>
    </div>
  );
}
